/**
 * Running totals over three spans, the way a car's trip computer keeps them.
 *
 *   trip  this drive, ended automatically by a long enough break
 *   tank  since the tank was last filled
 *   life  since the app first saw this car
 *
 * Two kinds of figure come out of it. Totals -- distance, fuel, time, moving
 * and idling -- are integrated from the live readings, and the useful averages
 * are ratios of those: consumption is fuel over distance, not the average of
 * the instantaneous litres-per-100 km, which would weight a minute at a red
 * light the same as a minute at 120 km/h. Everything else (engine speed,
 * coolant, boost) gets a plain time-weighted mean, which for those is exactly
 * what "average" should mean.
 *
 * Nothing is counted while the engine is stopped, so leaving the app open at
 * home does not dilute anything.
 */
import type { SignalStore } from './store.js';

export type JourneyScope = 'trip' | 'tank' | 'life';

export const JOURNEY_SCOPES: readonly JourneyScope[] = ['trip', 'tank', 'life'];

export const SCOPE_LABELS: Record<JourneyScope, string> = {
  trip: 'This trip',
  tank: 'Since fill-up',
  life: 'All time',
};

export const SCOPE_SHORT: Record<JourneyScope, string> = {
  trip: 'Trip',
  tank: 'Tank',
  life: 'All',
};

export interface JourneyTotals {
  /** Epoch ms when this span started counting. */
  startedAt: number;
  /** Epoch ms of the last sample folded in. */
  updatedAt: number;
  /** Seconds with the engine running. */
  seconds: number;
  movingS: number;
  idleS: number;
  /** Rolling in gear with a closed throttle. */
  coastS: number;
  distanceKm: number;
  /**
   * Of that distance, how much was covered while a fuel reading was coming in.
   * Consumption is fuel over this, not over the whole distance: a stretch with
   * no fuel data would otherwise drag the average down towards zero.
   */
  fuelDistanceKm: number;
  fuelL: number;
  /** Of that fuel, what was burnt while stationary. */
  idleFuelL: number;
  /** Integral of speed over time, for the average. */
  speedIntegral: number;
  maxSpeedKph: number;
  /** Drives folded in. The one in progress counts from its first moving sample. */
  trips: number;
}

/** Time-weighted accumulation for one signal over one span. */
export interface SignalAverage {
  seconds: number;
  sum: number;
  min: number;
  max: number;
}

export interface JourneySummary extends JourneyTotals {
  scope: JourneyScope;
  /** Fuel over distance, in L/100km; undefined before there is enough distance. */
  consumption?: number;
  /** Distance over engine-running time. */
  avgSpeedKph?: number;
  /** Distance over moving time: the average while actually moving. */
  avgMovingKph?: number;
  idlePct?: number;
  coastPct?: number;
  /** Litres per hour while stationary, which is what idling actually costs. */
  idleLph?: number;
}

/** What one completed drive is worth remembering. */
export interface TripRecord extends JourneyTotals {
  endedAt: number;
  consumption?: number;
  avgSpeedKph?: number;
}

export interface JourneyState {
  totals: Partial<Record<JourneyScope, JourneyTotals>>;
  /** Per-signal averages, by scope then signal key. */
  averages: Partial<Record<JourneyScope, Record<string, SignalAverage>>>;
}

export interface JourneySample {
  speedKph?: number;
  rpm?: number;
  accelerator?: number;
  /** Fuel flow in litres per hour. */
  lph?: number;
}

/**
 * A gap this long with the engine off ends the drive. Long enough to sit
 * through a fuel stop or a coffee without splitting one journey in two, short
 * enough that tomorrow morning is a new one.
 */
export const TRIP_GAP_MS = 15 * 60_000;

/** Below this the engine is not running and nothing is counted. */
const RUNNING_RPM = 300;
/** Below this the car is stationary rather than moving. */
const MOVING_KPH = 2;
/** A reading older than this is not current, so it is not averaged in. */
const FRESH_MS = 3000;
/** Distance before a consumption figure means anything. */
const MIN_CONSUMPTION_KM = 0.05;

export const emptyTotals = (startedAt = 0): JourneyTotals => ({
  startedAt,
  updatedAt: startedAt,
  seconds: 0,
  movingS: 0,
  idleS: 0,
  coastS: 0,
  distanceKm: 0,
  fuelDistanceKm: 0,
  fuelL: 0,
  idleFuelL: 0,
  speedIntegral: 0,
  maxSpeedKph: 0,
  trips: 0,
});

export class JourneyRecorder {
  private totalsByScope: Record<JourneyScope, JourneyTotals> = {
    trip: emptyTotals(),
    tank: emptyTotals(),
    life: emptyTotals(),
  };

  private averagesByScope: Record<JourneyScope, Map<string, SignalAverage>> = {
    trip: new Map(),
    tank: new Map(),
    life: new Map(),
  };

  /** Called when a drive has ended, with what it came to. */
  onTripComplete: ((trip: TripRecord) => void) | undefined;

  /** True once the current drive has actually moved, so it is worth recording. */
  private tripCounted = false;

  /**
   * Fold one polling interval into every span.
   *
   * `store` supplies the per-signal averages; only signals with a reading no
   * older than a few seconds are counted, so a PID that stopped answering does
   * not have its last value averaged in forever.
   */
  accumulate(dt: number, sample: JourneySample, store: SignalStore | undefined, now: number): void {
    if (!(dt > 0)) return;
    if ((sample.rpm ?? 0) < RUNNING_RPM) return;

    // A long silence means the previous drive ended: bank it and start again.
    const trip = this.totalsByScope.trip;
    if (trip.updatedAt > 0 && now - trip.updatedAt > TRIP_GAP_MS) this.endTrip(now);

    for (const scope of JOURNEY_SCOPES) {
      const t = this.totalsByScope[scope];
      if (t.startedAt === 0) t.startedAt = now;
      t.updatedAt = now;
      t.seconds += dt;

      const speed = sample.speedKph;
      if (speed !== undefined) {
        t.distanceKm += (speed / 3600) * dt;
        t.speedIntegral += speed * dt;
        if (speed > t.maxSpeedKph) t.maxSpeedKph = speed;
        if (speed < MOVING_KPH) {
          t.idleS += dt;
          if (sample.lph !== undefined) t.idleFuelL += (sample.lph / 3600) * dt;
        } else {
          t.movingS += dt;
          // Coasting: rolling in gear with a closed throttle, i.e. engine braking.
          if ((sample.accelerator ?? 100) < 3 && (sample.rpm ?? 0) > 900) t.coastS += dt;
        }
      }
      if (sample.lph !== undefined) {
        t.fuelL += (sample.lph / 3600) * dt;
        if (speed !== undefined) t.fuelDistanceKm += (speed / 3600) * dt;
      }
    }

    // The drive counts as one once it has moved at all.
    if (!this.tripCounted && (sample.speedKph ?? 0) >= MOVING_KPH) {
      this.tripCounted = true;
      for (const scope of JOURNEY_SCOPES) this.totalsByScope[scope].trips += 1;
    }

    if (store) this.accumulateSignals(dt, store, now);
  }

  private accumulateSignals(dt: number, store: SignalStore, now: number): void {
    for (const state of store.available()) {
      const value = state.value;
      if (value === undefined || !Number.isFinite(value)) continue;
      if (now - state.stats.lastUpdate > FRESH_MS) continue;
      for (const scope of JOURNEY_SCOPES) {
        const map = this.averagesByScope[scope];
        let entry = map.get(state.def.key);
        if (!entry) {
          entry = { seconds: 0, sum: 0, min: value, max: value };
          map.set(state.def.key, entry);
        }
        entry.seconds += dt;
        entry.sum += value * dt;
        if (value < entry.min) entry.min = value;
        if (value > entry.max) entry.max = value;
      }
    }
  }

  totals(scope: JourneyScope): Readonly<JourneyTotals> {
    return this.totalsByScope[scope];
  }

  summary(scope: JourneyScope): JourneySummary {
    return summarise(scope, this.totalsByScope[scope]);
  }

  /** Time-weighted mean of a signal over a span, if it has been seen there. */
  average(scope: JourneyScope, key: string): number | undefined {
    const entry = this.averagesByScope[scope].get(key);
    return entry && entry.seconds > 0 ? entry.sum / entry.seconds : undefined;
  }

  range(scope: JourneyScope, key: string): { min: number; max: number } | undefined {
    const entry = this.averagesByScope[scope].get(key);
    return entry && entry.seconds > 0 ? { min: entry.min, max: entry.max } : undefined;
  }

  /** Signals that have been averaged over a span. */
  averagedKeys(scope: JourneyScope): string[] {
    return [...this.averagesByScope[scope].keys()];
  }

  /** What a dashboard should show for this signal over this span. */
  value(scope: JourneyScope, key: string): number | undefined {
    return scopedMetric(this.summary(scope), key) ?? this.average(scope, key);
  }

  /**
   * Start a span over. Ending the trip span banks the drive first, so
   * "reset trip" from the UI still records what was driven.
   */
  reset(scope: JourneyScope, now = Date.now()): void {
    if (scope === 'trip') {
      this.endTrip(now);
      return;
    }
    this.totalsByScope[scope] = emptyTotals(now);
    this.averagesByScope[scope] = new Map();
  }

  /** Bank the drive in progress, if there was one, and begin a fresh one. */
  endTrip(now = Date.now()): TripRecord | undefined {
    const totals = this.totalsByScope.trip;
    const record: TripRecord | undefined = totals.seconds > 0
      ? { ...totals, endedAt: totals.updatedAt || now, ...ratios(totals) }
      : undefined;
    this.totalsByScope.trip = emptyTotals(now);
    this.averagesByScope.trip = new Map();
    this.tripCounted = false;
    if (record && record.distanceKm > 0) this.onTripComplete?.(record);
    return record;
  }

  export(): JourneyState {
    const totals: JourneyState['totals'] = {};
    const averages: JourneyState['averages'] = {};
    for (const scope of JOURNEY_SCOPES) {
      totals[scope] = { ...this.totalsByScope[scope] };
      const out: Record<string, SignalAverage> = {};
      for (const [key, entry] of this.averagesByScope[scope]) {
        if (entry.seconds > 0) out[key] = { ...entry };
      }
      averages[scope] = out;
    }
    return { totals, averages };
  }

  /** Take back what was stored. Anything malformed is dropped, not guessed at. */
  import(state: JourneyState | undefined): void {
    for (const scope of JOURNEY_SCOPES) {
      const totals = sanitizeTotals(state?.totals?.[scope]);
      if (totals) this.totalsByScope[scope] = totals;
      const map = new Map<string, SignalAverage>();
      const raw = state?.averages?.[scope];
      if (raw && typeof raw === 'object') {
        for (const [key, entry] of Object.entries(raw)) {
          const clean = sanitizeAverage(entry);
          if (clean && typeof key === 'string' && key.length <= 64) map.set(key, clean);
        }
      }
      this.averagesByScope[scope] = map;
    }
    this.tripCounted = false;
  }
}

/**
 * What a signal comes to over a span, where a plain average is the wrong sum.
 *
 * Consumption is the obvious one: the average of the instantaneous
 * litres-per-100 km would count a minute at a red light as heavily as a minute
 * at 120 km/h. The trip signals are totals rather than averages, and each span
 * has its own.
 */
export function scopedMetric(summary: JourneySummary, key: string): number | undefined {
  switch (key) {
    case 'consumption':
    case 'consumption_avg':
      return summary.consumption;
    case 'fuel_rate':
      return summary.seconds > 0 ? (summary.fuelL / summary.seconds) * 3600 : undefined;
    case 'trip_distance':
    case 'trip_distance_gps':
      return summary.distanceKm;
    case 'trip_fuel':
      return summary.fuelL;
    case 'trip_duration':
      return summary.seconds;
    case 'trip_avg_speed':
      return summary.avgSpeedKph;
    case 'trip_idle_pct':
      return summary.idlePct;
    case 'trip_coast_pct':
      return summary.coastPct;
    default:
      return undefined;
  }
}

export function summarise(scope: JourneyScope, totals: JourneyTotals): JourneySummary {
  return { scope, ...totals, ...ratios(totals) };
}

function ratios(t: JourneyTotals): Omit<JourneySummary, keyof JourneyTotals | 'scope'> {
  const out: Omit<JourneySummary, keyof JourneyTotals | 'scope'> = {};
  if (t.fuelDistanceKm > MIN_CONSUMPTION_KM) out.consumption = (t.fuelL / t.fuelDistanceKm) * 100;
  if (t.seconds > 0) {
    out.avgSpeedKph = t.speedIntegral / t.seconds;
    out.idlePct = (t.idleS / t.seconds) * 100;
    out.coastPct = (t.coastS / t.seconds) * 100;
  }
  if (t.movingS > 0) out.avgMovingKph = (t.distanceKm / t.movingS) * 3600;
  if (t.idleS > 30) out.idleLph = (t.idleFuelL / t.idleS) * 3600;
  return out;
}

const num = (v: unknown, fallback = 0): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;

function sanitizeTotals(raw: unknown): JourneyTotals | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const t = emptyTotals(num(r.startedAt));
  t.updatedAt = num(r.updatedAt, t.startedAt);
  t.seconds = Math.max(0, num(r.seconds));
  t.movingS = Math.max(0, num(r.movingS));
  t.idleS = Math.max(0, num(r.idleS));
  t.coastS = Math.max(0, num(r.coastS));
  t.distanceKm = Math.max(0, num(r.distanceKm));
  t.fuelDistanceKm = Math.max(0, num(r.fuelDistanceKm));
  t.fuelL = Math.max(0, num(r.fuelL));
  t.idleFuelL = Math.max(0, num(r.idleFuelL));
  t.speedIntegral = Math.max(0, num(r.speedIntegral));
  t.maxSpeedKph = Math.max(0, num(r.maxSpeedKph));
  t.trips = Math.max(0, Math.round(num(r.trips)));
  return t;
}

function sanitizeAverage(raw: unknown): SignalAverage | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const seconds = num(r.seconds);
  const sum = num(r.sum);
  if (!(seconds > 0) || !Number.isFinite(sum)) return undefined;
  const mean = sum / seconds;
  return { seconds, sum, min: num(r.min, mean), max: num(r.max, mean) };
}
